import type { CheckboxState } from '@sunwebgroup/uds-ui/checkbox'
import type { GroupOption } from '@/scopes/common/components/Filters/GroupedAirportFilter/types'
import { Checkbox } from '@sunwebgroup/uds-ui/checkbox'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import styles from './OptionGroup.module.css'

const NO_OPTIONS: GroupOption[] = []

export function OptionGroup({
  selectedValues,
  value,
  text,
  options = NO_OPTIONS,
  onChange,
  onChangeOption,
}: {
  value: string
  text: string
  selectedValues: string[]
  options: GroupOption[]
  onChange: (checked: CheckboxState) => void
  onChangeOption: ({ id, checked }: { id: string, checked: CheckboxState }) => void
}) {
  const elements = options.map(child => (
    <Option
      key={child.id}
      id={child.id}
      value={child.value}
      selectedValues={selectedValues}
      trailing={child.trailing}
      onChangeOption={onChangeOption}
    />
  ))
  const [selected, setIsSelected] = React.useState<CheckboxState>(false)
  const [halfSelected, setHalfSelected] = React.useState<boolean>(false)

  const myGroupIds = React.useMemo(() => options.map(option => option.id), [options])
  const containsAllIds = React.useMemo(
    () => myGroupIds.every(optionId => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds],
  )

  const containsSomeIds = React.useMemo(
    () => myGroupIds.some(optionId => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds],
  )

  React.useEffect(() => {
    if (containsAllIds) {
      setIsSelected(true)
      setHalfSelected(false)
    }
    else if (containsSomeIds) {
      setIsSelected('indeterminate')
      setHalfSelected(true)
    }
    else {
      setIsSelected(false)
      setHalfSelected(false)
    }
  }, [selectedValues, containsAllIds, containsSomeIds])

  return (
    <div className={styles.wrapper}>
      <label className={styles.checkboxWrapper}>
        <Checkbox
          value={value}
          checked={halfSelected ? 'indeterminate' : selected}
          onCheckedChange={(checked) => {
            setIsSelected(checked)
            setHalfSelected(false)
            onChange(checked)
          }}
        />
        <Typography variant="body-m-regular-tight" className={styles.text}>{text}</Typography>
      </label>
      {elements}
    </div>
  )
}

interface OptionProps {
  id: string
  value: string
  selectedValues: string[]
  trailing?: string
  onChangeOption: ({ id, checked }: { id: string, checked: CheckboxState }) => void
}

function Option({
  id,
  value,
  selectedValues,
  trailing,
  onChangeOption,
}: OptionProps) {
  const [isSelected, setIsSelected] = React.useState<CheckboxState>(false)
  React.useEffect(() => {
    setIsSelected(selectedValues.includes(id))
  }, [selectedValues, id])

  return (
    <div className={styles.optionWrapper}>
      <label className={cn(styles.checkboxWrapper)}>
        <Checkbox
          value={id}
          onCheckedChange={checked => onChangeOption({ id, checked })}
          checked={isSelected}
        />
        <Typography variant="body-m-regular-tight" className={styles.text}>{value}</Typography>
        {trailing && <Typography className={styles.trailing}>{trailing}</Typography>}
      </label>
    </div>
  )
}
